// src/utils/analytics.js - Enhanced HIPAA-compliant analytics utility
import { config } from '../config/config';

// Generate anonymous session ID (persisted in sessionStorage)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Generate anonymous patient hash (no PII)
const generatePatientHash = (patientData = {}) => {
  if (!patientData || typeof patientData !== 'object') return null;
  
  // Create hash from non-PII data only (age range, timestamp)
  const ageRange = patientData.age ? Math.floor(patientData.age / 10) * 10 : 'unknown';
  const hashInput = `${ageRange}_${new Date().getFullYear()}_${Math.random().toString(36).substr(2, 5)}`;
  
  return 'patient_' + btoa(hashInput).replace(/[^a-zA-Z0-9]/g, '').substr(0, 16);
};

// Core tracking function with enhanced options - Updated to use simple-track
export const trackEvent = (kind, options = {}) => {
  // Check if analytics is enabled
  if (!config?.features?.analytics && process.env.NODE_ENV !== 'production') {
    return Promise.resolve({ success: true, disabled: true });
  }
  
  try {
    const sessionId = getSessionId();
    
    const eventData = {
      kind,
      session_id: sessionId,
      doctor_id: options.doctor_id || null,
      meta: {
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent?.substr(0, 200),
        clinic_id: options.clinic_id || null,
        patient_hash: options.patient_hash || null,
        ...options.meta
      }
    };
    
    // REMOVED: Log that could expose patient data in eventData
    
    // Use simple-track endpoint (works with simple_events table)
    return fetch('/api/simple-track', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(eventData),
      keepalive: true
    }).then(response => {
      // Server logs will show success/failure - no need for browser console
      return { success: response.ok };
    }).catch(error => {
      // Server logs will show errors - no need for browser console
      return { success: false };
    });
  } catch (error) {
    // Server logs will handle error tracking
    return Promise.resolve({ success: false, error: error.message });
  }
};

// Specialized tracking functions for doctor analytics - Updated to use simple-track
export const trackDoctorSelection = async (doctorData, patientData = null) => {
  try {
    const sessionId = getSessionId();
    const patientHash = patientData ? generatePatientHash(patientData) : null;
    
    const eventData = {
      kind: 'doctor_matched',
      session_id: sessionId,
      doctor_id: doctorData.id,
      meta: {
        clinic_id: doctorData.clinic_id || null,
        patient_hash: patientHash,
        timestamp: new Date().toISOString(),
        action: 'doctor_selection'
      }
    };

    // REMOVED: Log that could expose doctor and patient selection data

    const response = await fetch('/api/simple-track', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(eventData),
      keepalive: true
    });

    return { success: response.ok, trackingId: sessionId + '_doctor_match' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const trackWhatsAppClick = async (doctorData, patientData = null) => {
  try {
    const sessionId = getSessionId();
    const patientHash = patientData ? generatePatientHash(patientData) : null;
    
    const trackingData = {
      doctor_id: doctorData.id,
      clinic_id: doctorData.clinic_id || null,
      session_id: sessionId,
      patient_hash: patientHash
    };

    const response = await fetch('/api/track-whatsapp-click', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-ID': sessionId
      },
      body: JSON.stringify(trackingData),
      keepalive: true
    });

    return { success: response.ok, trackingId: sessionId + '_whatsapp_click' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const trackDirectionsClick = async (doctorData, patientData = null) => {
  try {
    const sessionId = getSessionId();
    const patientHash = patientData ? generatePatientHash(patientData) : null;
    
    const trackingData = {
      doctor_id: doctorData.id,
      clinic_id: doctorData.clinic_id || null,
      session_id: sessionId,
      patient_hash: patientHash
    };

    const response = await fetch('/api/track-directions-click', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-ID': sessionId
      },
      body: JSON.stringify(trackingData),
      keepalive: true
    });

    return { success: response.ok, trackingId: sessionId + '_directions_click' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Session management
export const initializeSession = () => {
  const sessionId = getSessionId();
  trackEvent('session_started', {
    meta: {
      referrer: document.referrer,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  });
  return sessionId;
};

// Export session utilities
export const getAnalyticsSessionId = getSessionId;
export const createPatientHash = generatePatientHash;
